/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useEffect, useRef, useState } from "react";
import { useVideoPlayerDuration } from "../hooks/use-video-player-helpers";

export function VideoProgressBar({
  onHoverChange,
}: {
  onHoverChange?: (hovered: boolean) => void;
}) {
  const { currentTime, duration, setCurrentTime, formatTime, bufferedTime } =
    useVideoPlayerDuration();
  const barRef = useRef<HTMLDivElement>(null);

  const [isHovered, setIsHovered] = useState(false);
  const [hoverX, setHoverX] = useState<number | null>(null);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const percent = duration ? (currentTime / duration) * 100 : 0;
  const bufferedPercent = duration ? (bufferedTime / duration) * 100 : 0;

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!barRef.current || !duration) return;
    const rect = barRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newTime = (x / rect.width) * duration;
    setCurrentTime(newTime);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!barRef.current || !duration) return;
    const rect = barRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const time = (x / rect.width) * duration;
    setHoverX(x);
    setHoverTime(time);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (onHoverChange) onHoverChange(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setHoverX(null);
    setHoverTime(null);
    if (onHoverChange) onHoverChange(false);
  };

  // Helper to calculate time from event (mouse or touch)
  const getTimeFromEvent = (clientX: number) => {
    if (!barRef.current || !duration) return null;
    const rect = barRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    return (x / rect.width) * duration;
  };

  // Mouse events
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
    const newTime = getTimeFromEvent(e.clientX);
    if (newTime !== null) setCurrentTime(newTime);
    document.addEventListener("mousemove", handleDocumentMouseMove);
    document.addEventListener("mouseup", handleDocumentMouseUp);
  };

  const handleDocumentMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    const newTime = getTimeFromEvent(e.clientX);
    if (newTime !== null) setCurrentTime(newTime);
  };

  const handleDocumentMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleDocumentMouseMove);
    document.removeEventListener("mouseup", handleDocumentMouseUp);
  };

  // Touch events
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);
    const touch = e.touches[0];
    const newTime = getTimeFromEvent(touch.clientX);
    if (newTime !== null) setCurrentTime(newTime);
    document.addEventListener("touchmove", handleDocumentTouchMove);
    document.addEventListener("touchend", handleDocumentTouchEnd);
  };

  const handleDocumentTouchMove = (e: TouchEvent) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const newTime = getTimeFromEvent(touch.clientX);
    if (newTime !== null) setCurrentTime(newTime);
  };

  const handleDocumentTouchEnd = (e: TouchEvent) => {
    setIsDragging(false);
    document.removeEventListener("touchmove", handleDocumentTouchMove);
    document.removeEventListener("touchend", handleDocumentTouchEnd);
  };

  // Clean up listeners on unmount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleDocumentMouseMove);
      document.removeEventListener("mouseup", handleDocumentMouseUp);
      document.removeEventListener("touchmove", handleDocumentTouchMove);
      document.removeEventListener("touchend", handleDocumentTouchEnd);
    };
  }, []);

  return (
    <div
      className={`video-player-2-seekbar relative${
        isHovered ? " hovered" : ""
      }`}
      ref={barRef}
      onClick={handleSeek}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      role="slider"
      aria-valuenow={currentTime}
      aria-valuemin={0}
      aria-valuemax={duration}
      tabIndex={0}
      style={{ cursor: isDragging ? "grabbing" : "pointer", height: 8 }}>
      {/* Buffered bar */}
      <div
        className="video-player-2-seekbar__buffered"
        style={{ width: `${bufferedPercent}%`, zIndex: 1, opacity: 0.5 }}
      />
      {/* Progress bar */}
      <div
        className="video-player-2-seekbar__progress"
        style={{ width: `${percent}%`, zIndex: 2 }}
      />
      {/* Hover time tooltip */}
      {isHovered && hoverX !== null && hoverTime !== null && (
        <div
          className="absolute z-10 px-2 py-1 text-xs text-white bg-black bg-opacity-80 rounded pointer-events-none"
          style={{ left: hoverX, top: -30, transform: "translateX(-50%)" }}>
          {formatTime(hoverTime)}
        </div>
      )}
    </div>
  );
}
